import AirDatepicker from "air-datepicker";
import localeEn from "air-datepicker/locale/en";
import localeNl from "air-datepicker/locale/nl";
import localeAr from "air-datepicker/locale/ar";
import localeEs from "air-datepicker/locale/es";
import localeFr from "air-datepicker/locale/fr";
import localeTr from "air-datepicker/locale/tr";
import { sendAppsignalError } from "../modules/utils";

export default class EventPage {
  constructor(node) {
    this.element = node;
    this.url = this.element.dataset.url;
    this.fullLocale = null;
    this.airDatepickerLocale = null;
    this.datePickerElement = document.getElementById("selected-date");
    this.timeZoneSelect = document.querySelector(".js-time-zone-select");
    this.timeZoneButton = null;
    this.trueIfMobile = window.matchMedia("(max-width: 767.98px)").matches;
    this.currentTimeZone = this.getCurrentTimeZone();
    this.selectedTimeZone = this.currentTimeZone;
    this.selectedGmtOffset = this.getGmtOffset(this.selectedTimeZone);
    this.filterForm = this.element.querySelector(".js-event-filter");
    this.startDate = new Date();
    this.filterTimeOut = null;
    this.viaPopState = false;
    this.agendaWrapper = this.element.querySelector(".js-agenda-wrapper");
    this.addToOwnCalendar = this.element.querySelector(".js-add-to-own-calendar");
    this.tabs = this.element.querySelectorAll(".js-agenda-tabs");
    this.filterElements = this.filterForm.querySelectorAll('input[type="checkbox"], input[type="radio"]');

    this.calendarDefaultOptions = {
      firstDay: 1,
      timeZone: this.selectedTimeZone,
      scrollTime: "09:00:00",
      titleFormat: {
        year: "numeric",
        month: "long",
        day: "numeric",
      },
      customButtons: {
        prev: {
          click: () => {
            this.nextOrPrevClicked = true;
            this.calendar.prev();
          },
        },
        next: {
          click: () => {
            this.nextOrPrevClicked = true;
            this.calendar.next();
          },
        },
        today: {
          // We use query selector since we don't have the element assigned yet
          text: document.querySelector("[data-today-text]").dataset.todayText,
          click: () => {
            this.nextOrPrevClicked = true;
            this.calendar.today();
          },
        },
      },
      headerToolbar: {
        left: "title",
        center: "timeZoneButton",
        right: "prev today next",
      },
    };
  }

  prepare() {
    let urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach(function (value, key) {
      var values = value.split(",");
      values.forEach(function (val) {
        const filterElement = document.querySelector(`input[name="${key}"][value="${val}"]`);
        if (filterElement) {
          filterElement.checked = true;
          const collapseElement = filterElement.closest(".collapse");
          if (collapseElement) {
            collapseElement.classList.add("show");
          }
          setTimeout(() => {
            filterElement.dispatchEvent(new Event("change"));
          }, 0);
        }
      });
    });
    // set the start date based on the URL parameter
    let startDate = urlParams.get("start");

    if (startDate) {
      var date = new Date(startDate);
      if (date instanceof Date && !isNaN(date.valueOf())) {
        this.startDate = date;
      } else {
        console.error("Invalid start date format in URL parameter: " + startDate);
      }
    }

    this.setLocale(document.documentElement.lang);

    this.fetchFromUrl(true);
  }

  initialize() {
    window.addEventListener("popstate", () => {
      const urlParams = new URLSearchParams(window.location.search);
      const startDate = urlParams.get("start");

      this.filterElements.forEach((filterElement) => {
        const values = urlParams.get(filterElement.name);

        if (values) {
          const valuesArray = values.split(",");
          filterElement.checked = valuesArray.includes(filterElement.value);
        } else {
          filterElement.checked = false;
        }
      });

      this.timeZoneSelected = true;
      this.timeZoneSelect.value = urlParams.get("timezone") || this.currentTimeZone;
      this.selectedTimeZone = this.timeZoneSelect.value;
      this.calendar.setOption("timeZone", this.selectedTimeZone);
      this.updateCalendarTimezone();

      if (startDate) {
        const startDateObj = new Date(startDate);
        if (startDateObj instanceof Date && !isNaN(startDateObj.valueOf())) {
          const weekStartAndEnd = this.getWeekRange(startDateObj);
          this.datePicker.selectDate(weekStartAndEnd, { silent: true });

          this.viaPopState = true;
          this.calendar.gotoDate(startDateObj, false);
        }
      }
    });

    this.filterElements.forEach((filterElement) => {
      filterElement.addEventListener("change", () => {
        this.agendaWrapper.classList.add("is-loading");
        this.updateUrl();
      });
    });

    this.datePicker = new AirDatepicker(this.datePickerElement, {
      isMobile: this.trueIfMobile,
      locale: this.airDatepickerLocale,
      startDate: this.startDate,
      inline: true,
      firstDay: 1,
      range: true,
      dynamicRange: false,
      navTitles: {
        days: "<strong>MMMM yyyy</strong><i class='icon icon-chevron-down-bold'>",
        months: "<strong>MMMM yyyy</strong><i class='icon icon-chevron-down-bold'></i>",
        years: "<strong>yyyy1 - yyyy2</strong>",
      },
      prevHtml: "<i class='icon icon-chevron-left-bold'></i>",
      nextHtml: "<i class='icon icon-chevron-right-bold'></i>",
      onSelect: ({ date }) => {
        const weekStartAndEnd = this.getWeekRange(date[0]);

        this.datePicker.selectDate(weekStartAndEnd, { silent: true });

        this.airDatepickerOnSelect(date[0]);
      },
    });

    this.datePickerElement.classList.remove("content-wrapper", "is-loading");

    if (this.trueIfMobile) {
      window.addEventListener("scroll", () => {
        requestAnimationFrame(() => {
          // hide datepicker on mobile when scrolling
          this.datePicker.hide();
        });
      });

      window.addEventListener("touchstart", (e) => {
        // hide datepicker on mobile when clicking outside the datepicker
        if (!this.datePickerElement.contains(e.target)) {
          this.datePicker.hide();
        }
      });
    }
  }

  getCurrentTimeZone() {
    const timezones = [
      "Europe/Brussels",
      "America/Anchorage",
      "America/Puerto_Rico",
      "America/Denver",
      "Europe/Dublin",
      "Pacific/Honolulu",
      "Europe/Istanbul",
      "Europe/London",
      "America/Los_Angeles",
      "America/Mexico_City",
      "Pacific/Pago_Pago",
      "America/New_York",
    ];

    const timezoneParam = new URLSearchParams(window.location.search).get("timezone");

    let timezone = timezoneParam ? timezoneParam : Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!timezones.includes(timezone)) {
      timezone = "Europe/Brussels";
    }

    return timezone;
  }

  getWeekRange(date) {
    date = new Date(date);

    const day = date.getDay();
    const diff = date.getDate() - day + (day == 0 ? -6 : 1);

    // Returns monday and sunday in an array
    return [new Date(date.setDate(diff)), new Date(date.setDate(diff + 6))];
  }

  setLocale(locale) {
    switch (locale) {
      case "en":
        this.fullLocale = "en-US";
        this.airDatepickerLocale = localeEn;
        break;
      case "nl":
        this.fullLocale = "nl-NL";
        this.airDatepickerLocale = localeNl;
        break;
      case "ar":
        this.fullLocale = "ar-EG";
        this.airDatepickerLocale = localeAr;
        break;
      case "es":
        this.fullLocale = "es-ES";
        this.airDatepickerLocale = localeEs;
        break;
      case "fr":
        this.fullLocale = "fr-FR";
        this.airDatepickerLocale = localeFr;
        break;
      case "tr":
        this.fullLocale = "tr-TR";
        this.airDatepickerLocale = localeTr;
        break;
      default:
        this.fullLocale = "en-US";
        this.airDatepickerLocale = localeEn;
    }
  }

  getGmtOffset(timeZone, date = new Date()) {
    if (!timeZone || timeZone === "") {
      timeZone = "Europe/Brussels";
    }
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: timeZone,
      hour12: false,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(date);
    const gmtOffsetPart = parts.find((part) => part.type === "timeZoneName").value;
    const gmtOffset = gmtOffsetPart.replace("GMT", "");

    return gmtOffset;
  }

  updateUrl() {
    let filterValues = {};
    const currentUrl = new URL(window.location);
    const searchParams = new URLSearchParams(currentUrl.search);

    this.filterElements.forEach((filterElement) => {
      if (filterElement.checked) {
        if (!filterValues[filterElement.name]) {
          filterValues[filterElement.name] = [];
        }
        filterValues[filterElement.name].push(filterElement.value);
      }
    });

    for (let filterName in filterValues) {
      searchParams.set(filterName, filterValues[filterName].join(","));
    }

    this.filterElements.forEach((filterElement) => {
      if (!filterValues[filterElement.name]) {
        searchParams.delete(filterElement.name);
      }
    });

    let newUrl = currentUrl.pathname + "?" + searchParams.toString();
    window.history.pushState({}, "", newUrl);

    this.tabs.forEach((tab) => {
      tab.querySelectorAll("a").forEach((link) => {
        link.href = link.href.split("?")[0] + "?" + searchParams.toString();
      });
    });

    clearTimeout(this.filterTimeOut);

    this.filterTimeOut = setTimeout(() => {
      this.fetchFromUrl();
    }, 500);
  }

  updateHistory({ start = null }) {
    const urlWithSearchParams = new URL(window.location);

    urlWithSearchParams.searchParams.set("timezone", this.selectedTimeZone);

    if (start) {
      const startDate = start.split("T")[0];
      urlWithSearchParams.searchParams.set("start", startDate);
    }

    window.history.pushState({}, "", urlWithSearchParams);

    this.tabs.forEach((tab) => {
      tab.querySelectorAll("a").forEach((link) => {
        link.href = link.href.split("?")[0] + "?" + urlWithSearchParams.searchParams.toString();
      });
    });
  }

  handleNewDateSet(args) {
    let { start, startStr } = args;

    clearTimeout(this.dateSetTimeOut);

    const weekStartAndEnd = this.getWeekRange(start);

    if (this.initialLoad) {
      this.datePicker.selectDate(weekStartAndEnd, { silent: true });
      this.initialLoad = false;
      return;
    }

    if (this.viaPopState) {
      this.dateSetTimeOut = setTimeout(() => {
        this.fetchFromUrl();
      }, 500);
      this.viaPopState = false;
      this.timeZoneSelected = false;
      return;
    }

    if (this.timeZoneSelected || this.nextOrPrevClicked) {
      this.datePicker.selectDate(weekStartAndEnd, { silent: true });
      this.timeZoneSelected = false;
      this.nextOrPrevClicked = false;
    } else {
      this.datePicker.selectDate(weekStartAndEnd[0], { silent: true });
      this.timeZoneSelected = false;
    }

    this.updateHistory({ start: startStr });
    this.dateSetTimeOut = setTimeout(() => {
      this.fetchFromUrl();
    }, 500);
  }

  addTimeZoneSelect() {
    let calendarToolbarElement;
    const calendarTimeZoneSelectElement = this.timeZoneSelect.cloneNode(true);

    calendarTimeZoneSelectElement.classList.remove("d-none");

    this.timeZoneSelect.remove();
    this.timeZoneSelect = calendarTimeZoneSelectElement;
    this.timeZoneSelect.value = this.selectedTimeZone;

    if (this.calendarElement) {
      calendarToolbarElement = this.calendarElement.querySelector(".fc-header-toolbar :nth-child(2)");
    }
    if (this.eventListElement) {
      calendarToolbarElement = this.eventListElement.querySelector(".fc-header-toolbar :nth-child(2)");
    }

    calendarToolbarElement.classList.add("ms-2", "ms-md-4", "me-auto", "position-relative", "d-block");
    calendarToolbarElement.appendChild(this.timeZoneSelect);

    this.timeZoneSelect.addEventListener("change", (event) => {
      this.timeZoneSelected = true;

      this.selectedTimeZone = event.target.value;
      this.calendar.setOption("timeZone", this.selectedTimeZone);
      this.updateCalendarTimezone();
    });
  }

  fetchFromUrl(firstLoad = false) {
    this.agendaWrapper.classList.add("is-loading");

    const currentUrl = new URL(window.location);
    const searchParams = new URLSearchParams(currentUrl.search);

    if (!firstLoad) {
      this.removeAllEvents();
    } else {
      this.initialLoad = true;
    }

    fetch(this.url + "?" + searchParams.toString(), {
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        this.response = response;

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        return response.json();
      })
      .then((data) => {
        const { url, events } = data;
        this.events = events;

        this.addToOwnCalendar.href = url;

        if (firstLoad) {
          this.initialize();
        } else {
          this.addEvents();
        }
      })
      .catch((e) => {
        console.error("Error fetching events: ", e);
        sendAppsignalError(e, "EventPage", {
          response: this.response,
        });
      })
      .finally(() => {
        this.agendaWrapper.classList.remove("is-loading");
      });
  }
}
